import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import ExpertCard from "@/components/ExpertCard";
import { AddToCalendar } from "@/components/AddToCalendar";
import { Calendar, Clock, Star, Bookmark, ArrowRight, Video, CheckCircle, XCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function ExplorerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [saved, setSaved] = useState([]);
  const [aiQuery, setAiQuery] = useState("");
  const [aiRecs, setAiRecs] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bRes, sRes] = await Promise.all([
          fetch(`${API}/explorer/bookings`, { credentials: "include" }),
          fetch(`${API}/explorer/saved`, { credentials: "include" }),
        ]);
        if (bRes.ok) setBookings(await bRes.json());
        if (sRes.ok) setSaved(await sRes.json());
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAiRecommend = async () => {
    if (!aiQuery.trim()) return;
    setAiLoading(true);
    try {
      const res = await fetch(`${API}/ai/recommend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: aiQuery }),
      });
      if (res.ok) {
        const data = await res.json();
        setAiRecs(data.recommendations || []);
      }
    } finally {
      setAiLoading(false);
    }
  };

  const upcoming = bookings.filter((b) => b.status === "confirmed" && b.scheduled_at);
  const pending = bookings.filter((b) => b.status === "pending_payment");
  const past = bookings.filter((b) => ["completed", "cancelled"].includes(b.status));

  const STATUS_CONFIG = {
    confirmed: { label: "Confirmed", color: "bg-[#064E3B]/10 text-[#064E3B]", icon: CheckCircle },
    pending_payment: { label: "Payment Pending", color: "bg-amber-50 text-amber-700", icon: Clock },
    completed: { label: "Completed", color: "bg-[#F4F4F1] text-[#52525B]", icon: CheckCircle },
    cancelled: { label: "Cancelled", color: "bg-red-50 text-red-600", icon: XCircle },
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <p className="section-label mb-1">Explorer Dashboard</p>
              <h1 className="text-3xl font-normal text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Welcome back, {user?.name?.split(" ")[0]}
              </h1>
            </div>
            <Link to="/discover" className="btn-primary flex items-center gap-2 text-sm" data-testid="find-experts-btn">
              Find Experts <ArrowRight size={14} />
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Total Bookings", value: bookings.length },
              { label: "Upcoming Sessions", value: upcoming.length },
              { label: "Experts Saved", value: saved.length },
              { label: "Sessions Completed", value: past.filter(b => b.status === "completed").length },
            ].map((s) => (
              <div key={s.label} className="card-flat text-center">
                <p className="text-2xl font-bold text-[#0F172A] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                <p className="text-xs text-[#52525B]">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Tabs defaultValue="bookings">
                <TabsList className="bg-[#F4F4F1] border border-[#E4E4E1] mb-5">
                  <TabsTrigger value="bookings" data-testid="tab-bookings">My Bookings</TabsTrigger>
                  <TabsTrigger value="saved" data-testid="tab-saved">Saved Experts</TabsTrigger>
                </TabsList>

                <TabsContent value="bookings">
                  {loading ? (
                    <div className="space-y-3">
                      {[1,2,3].map(i => <div key={i} className="h-24 bg-white border border-[#E4E4E1] rounded-sm animate-pulse" />)}
                    </div>
                  ) : bookings.length === 0 ? (
                    <div className="card-flat text-center py-12">
                      <Calendar size={32} className="text-[#A1A1AA] mx-auto mb-3" />
                      <p className="text-[#52525B] text-sm mb-4">No bookings yet.</p>
                      <Link to="/discover" className="btn-primary text-sm">Browse Experts</Link>
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="bookings-list">
                      {bookings.map((b) => {
                        const cfg = STATUS_CONFIG[b.status] || STATUS_CONFIG.pending_payment;
                        const StatusIcon = cfg.icon;
                        return (
                          <div key={b.booking_id} className="card-flat p-4" data-testid={`booking-${b.booking_id}`}>
                            <div className="flex items-start gap-4">
                              <img
                                src={b.expert_info?.profile_image || `https://ui-avatars.com/api/?name=${encodeURIComponent(b.expert_name || "")}&background=0F172A&color=fff`}
                                alt={b.expert_name}
                                className="w-12 h-12 rounded-sm object-cover border border-[#E4E4E1] shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <p className="font-semibold text-sm text-[#0F172A]">{b.service_name}</p>
                                    <p className="text-xs text-[#52525B]">with {b.expert_name || b.expert_info?.name}</p>
                                  </div>
                                  <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${cfg.color} flex items-center gap-1 shrink-0`}>
                                    <StatusIcon size={11} /> {cfg.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 mt-2 text-xs text-[#A1A1AA]">
                                  <span className="flex items-center gap-1"><Clock size={11} />{b.duration} mins</span>
                                  <span className="font-semibold text-[#0F172A]">₹{b.amount?.toLocaleString("en-IN")}</span>
                                  {b.scheduled_at && <span className="flex items-center gap-1"><Calendar size={11} />{new Date(b.scheduled_at).toLocaleDateString("en-IN")}</span>}
                                </div>
                                {b.status === "confirmed" && b.google_meet_link && (
                                  <a href={b.google_meet_link} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 mt-2 text-xs text-[#064E3B] font-semibold hover:underline">
                                    <Video size={12} /> Join Meeting
                                  </a>
                                )}
                                {b.status === "confirmed" && (
                                  <AddToCalendar booking={b} withName={b.expert_name || b.expert_info?.name} className="mt-2" />
                                )}
                                {b.status === "pending_payment" && (
                                  <button
                                    onClick={() => navigate(`/book/${b.service_id}?expert=${b.council_member_id}`)}
                                    className="mt-2 text-xs text-amber-700 font-semibold hover:underline"
                                  >
                                    Complete Payment
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="saved">
                  {saved.length === 0 ? (
                    <div className="card-flat text-center py-12">
                      <Bookmark size={32} className="text-[#A1A1AA] mx-auto mb-3" />
                      <p className="text-[#52525B] text-sm mb-4">No saved experts yet.</p>
                      <Link to="/discover" className="btn-primary text-sm">Explore Experts</Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {saved.map((exp) => <ExpertCard key={exp.user_id} expert={exp} />)}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            {/* AI Sidebar */}
            <div className="space-y-4">
              <div className="card-flat">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles size={16} className="text-[#064E3B]" />
                  <h3 className="text-sm font-semibold text-[#0F172A]">AI Expert Match</h3>
                </div>
                <textarea
                  rows={3}
                  placeholder="Describe what you need help with..."
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  className="w-full border border-[#E4E4E1] text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#0F172A] resize-none mb-3"
                  data-testid="ai-query-input"
                />
                <button
                  onClick={handleAiRecommend}
                  disabled={aiLoading || !aiQuery.trim()}
                  className="w-full btn-primary text-sm py-2.5"
                  data-testid="ai-match-btn"
                >
                  {aiLoading ? "Finding matches..." : "Find Matches"}
                </button>
              </div>

              {aiRecs.length > 0 && (
                <div className="space-y-3" data-testid="ai-recommendations">
                  {aiRecs.slice(0, 3).map((rec) => (
                    <div key={rec.user_id} className="card-flat p-4 cursor-pointer hover:border-[#0F172A] transition-colors"
                      onClick={() => navigate(`/expert/${rec.user_id}`)}>
                      <div className="flex gap-3">
                        <img src={rec.profile_image || `https://ui-avatars.com/api/?name=${rec.name}&background=0F172A&color=fff`} alt={rec.name} className="w-10 h-10 rounded-sm object-cover border border-[#E4E4E1]" />
                        <div>
                          <p className="text-xs font-semibold text-[#0F172A]">{rec.name}</p>
                          <p className="text-[10px] text-[#52525B] line-clamp-2">{rec.ai_reason || rec.headline}</p>
                          {rec.match_score && <span className="text-[9px] bg-[#064E3B]/10 text-[#064E3B] px-1.5 py-0.5 rounded-sm font-bold mt-1 inline-block">{rec.match_score}% match</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
